/**
 * JourneyRecordForm: record, correct or back-fill one journey step (DA, E1 to E5) for a CCMS member or a
 * call centre person. Completed steps take an exact date or only the year (historical back-fill); booked steps
 * take the date they are booked for. The link title and date describe the event, meeting or course it was.
 *
 * Used by the Journey page (Record tab, with a member picker), the contact drawer (for the person) and the
 * member profile's Journey card (JourneyRecordModal).
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { Stack, Group, Button, Select, SegmentedControl, NumberInput, TextInput, Textarea, DatePickerInput, SimpleGrid, Alert, Modal, Text } from '@/components/ui';
import { useCallCentreAdminService, type CallCentreJourneyView, type JourneyStepRecordRequest, type JourneyBadgeView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useCallCentreLookups, useJourneySteps, lookupOptions } from './useCallCentreData';
import { CALL_CENTRE_JOURNEY_STEP_STATUS, callCentreErrorMessage, callCentreKeys, dateToYmd, ymdToDate } from './callCentreUtils';

export interface JourneyTarget {
  memberId?: number | null;
  personId?: number | null;
  name?: string | null;
}

interface JourneyRecordFormProps {
  target: JourneyTarget | null;
  /** Start from this step (and its current entry when given). */
  initialStepId?: number | null;
  initialEntry?: JourneyBadgeView['entry'];
  onSaved?: (journey: CallCentreJourneyView) => void;
  onCancel?: () => void;
  helpId?: string;
}

const S = CALL_CENTRE_JOURNEY_STEP_STATUS;

export function JourneyRecordForm({ target, initialStepId, initialEntry, onSaved, onCancel, helpId }: JourneyRecordFormProps) {
  const admin = useCallCentreAdminService();
  const queryClient = useQueryClient();
  const lookups = useCallCentreLookups();
  const { steps } = useJourneySteps();
  const [stepId, setStepId] = useState<number | null>(initialStepId ?? null);
  const [statusId, setStatusId] = useState<number>(initialEntry?.statusID ?? S.Completed);
  const [whenMode, setWhenMode] = useState<'date' | 'year'>(initialEntry?.completedYear && !initialEntry?.completedOn ? 'year' : 'date');
  const [completedOn, setCompletedOn] = useState<Date | null>(ymdToDate(initialEntry?.completedOn));
  const [completedYear, setCompletedYear] = useState<number | ''>(initialEntry?.completedYear ?? '');
  const [bookedFor, setBookedFor] = useState<Date | null>(ymdToDate(initialEntry?.bookedFor));
  const [linkTitle, setLinkTitle] = useState(initialEntry?.linkTitle ?? '');
  const [linkDate, setLinkDate] = useState<Date | null>(ymdToDate(initialEntry?.linkDate));
  const [note, setNote] = useState(initialEntry?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const statusOptions = lookupOptions(lookups.data?.journeyStepStatuses);
  const statuses = statusOptions.length
    ? statusOptions
    : [
        { value: String(S.Booked), label: 'Booked' },
        { value: String(S.Completed), label: 'Completed' },
        { value: String(S.Cancelled), label: 'Cancelled' },
        { value: String(S.NoShow), label: 'No-show' },
      ];
  const thisYear = new Date().getFullYear();

  const save = useMutation({
    mutationFn: (req: JourneyStepRecordRequest) => {
      if (target?.memberId) return admin.recordMemberJourneyStep(target.memberId, req);
      return admin.recordPersonJourneyStep(target?.personId as number, req);
    },
    onSuccess: (journey) => {
      if (journey.memberID) queryClient.setQueryData(callCentreKeys.memberJourney(journey.memberID), journey);
      if (target?.memberId) queryClient.invalidateQueries({ queryKey: callCentreKeys.memberJourney(target.memberId) });
      queryClient.invalidateQueries({ queryKey: callCentreKeys.journeyBoard() });
      queryClient.invalidateQueries({ queryKey: ['callcentre', 'contact'] });
      queryClient.invalidateQueries({ queryKey: callCentreKeys.dashboard() });
      toast.success('Journey step saved', target?.name ?? undefined);
      onSaved?.(journey);
    },
    onError: (err) => setError(callCentreErrorMessage(err, "Couldn't save the journey step.")),
  });

  const submit = () => {
    setError(null);
    if (!target?.memberId && !target?.personId) {
      setError('Pick the person first.');
      return;
    }
    if (!stepId) {
      setError('Pick the step.');
      return;
    }
    if (statusId === S.Completed && whenMode === 'year' && completedYear !== '' && (completedYear < 1950 || completedYear > thisYear)) {
      setError(`The year must be between 1950 and ${thisYear}.`);
      return;
    }
    save.mutate({
      stepID: stepId,
      statusID: statusId,
      completedOn: statusId === S.Completed && whenMode === 'date' ? dateToYmd(completedOn) ?? null : null,
      completedYear: statusId === S.Completed && whenMode === 'year' && completedYear !== '' ? Number(completedYear) : null,
      bookedFor: statusId === S.Booked ? dateToYmd(bookedFor) ?? null : null,
      linkTitle: linkTitle.trim() || null,
      linkDate: dateToYmd(linkDate) ?? null,
      note: note.trim() || null,
    });
  };

  return (
    <Stack gap="sm" data-help-id={helpId}>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Select
          label="Step"
          placeholder="Pick a step"
          data={steps.filter((s) => s.isActive || s.stepID === stepId).map((s) => ({ value: String(s.stepID), label: `${s.code}: ${s.name}` }))}
          value={stepId ? String(stepId) : null}
          onChange={(v) => setStepId(v ? Number(v) : null)}
          required
        />
        <Select label="Status" data={statuses} value={String(statusId)} onChange={(v) => v && setStatusId(Number(v))} allowDeselect={false} />
      </SimpleGrid>
      {statusId === S.Completed && (
        <Stack gap={6}>
          <SegmentedControl
            size="xs"
            data={[
              { value: 'date', label: 'Exact date' },
              { value: 'year', label: 'Only the year' },
            ]}
            value={whenMode}
            onChange={(v) => setWhenMode(v as 'date' | 'year')}
          />
          {whenMode === 'date' ? (
            <DatePickerInput label="Completed on" placeholder="Pick the date" value={completedOn} onChange={(v) => setCompletedOn(v ? new Date(v as Date) : null)} clearable maxDate={new Date()} valueFormat="D MMM YYYY" />
          ) : (
            <NumberInput label="Completed in" description="For people who did it years ago" placeholder={String(thisYear)} value={completedYear} onChange={(v) => setCompletedYear(v === '' ? '' : Number(v))} min={1950} max={thisYear} allowDecimal={false} />
          )}
        </Stack>
      )}
      {statusId === S.Booked && <DatePickerInput label="Booked for" placeholder="Pick the date" value={bookedFor} onChange={(v) => setBookedFor(v ? new Date(v as Date) : null)} clearable valueFormat="D MMM YYYY" />}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput label="Event, meeting or course" placeholder="For example: Discover Acts, March" value={linkTitle} onChange={(e) => setLinkTitle(e.currentTarget.value)} maxLength={300} />
        <DatePickerInput label="Its date" placeholder="Optional" value={linkDate} onChange={(v) => setLinkDate(v ? new Date(v as Date) : null)} clearable valueFormat="D MMM YYYY" />
      </SimpleGrid>
      <Textarea label="Note" placeholder="Optional" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} maxLength={1000} />
      {error && <Alert color="red">{error}</Alert>}
      <Group justify="flex-end" gap="sm">
        {onCancel && (
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button leftSection={<IconDeviceFloppy size={16} />} loading={save.isPending} onClick={submit} disabled={!target?.memberId && !target?.personId}>
          Save step
        </Button>
      </Group>
    </Stack>
  );
}

interface JourneyRecordModalProps {
  opened: boolean;
  onClose: () => void;
  target: JourneyTarget | null;
  initialStepId?: number | null;
  initialEntry?: JourneyBadgeView['entry'];
}

export function JourneyRecordModal({ opened, onClose, target, initialStepId, initialEntry }: JourneyRecordModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title={target?.name ? `Journey: ${target.name}` : 'Record a journey step'} size="lg">
      {opened && (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Record a step they have booked or completed, or fill in one they did before (only the year is fine).
          </Text>
          <JourneyRecordForm key={`${initialStepId ?? 'new'}`} target={target} initialStepId={initialStepId} initialEntry={initialEntry} onSaved={onClose} onCancel={onClose} />
        </Stack>
      )}
    </Modal>
  );
}
